import type { TrainingSessionData, IMediaDevices } from "@workspace/types";
import { inject, injectable } from "inversify";
import { makeAutoObservable, runInAction } from "mobx";
import { AvaturnHead } from "@avaturn-live/web-sdk";
import { waitForAvatarSession } from "@workspace/core";
import {
  createPublicTrainingSession,
  completePublicTrainingSession,
} from "../api/training-sessions";
import { trackSessionEvent } from "../analytics/events";
import { LandingServiceID } from "./symbols";
import type { IRemainingTime } from "@/lib/types/training";
import type { ILandingSessionManager } from "@/lib/types/store";
import type { CoachTurn, ICoachManager } from "@/lib/types/coach";

/**
 * Landing Session Store - Implements ISessionManager for public training
 *
 * Manages:
 * - Session creation and lifecycle
 * - Media device permissions and selection
 * - AvaturnHead SDK integration
 * - Feedback polling coordination (handled in components)
 *
 * Key differences from Dashboard:
 * - Uses public API endpoints (no authentication)
 * - 10-minute session timer with auto-end
 * - Same AvaturnHead SDK integration
 * - Same live coach events (coach_mode_activated / coach_text)
 */
@injectable()
export class LandingSessionStore implements ILandingSessionManager {
  sessionId: string | null = null;
  state: "initial" | "ready" | "started" | "finished" | "media-error" =
    "initial";
  avatarMediaStream: MediaStream | null = null;
  userVideoStream: MediaStream | null = null;
  isAvatarSpeaking: boolean = false;
  coachTurns: CoachTurn[] = [];
  mediaPermissions: { audio: boolean; video: boolean } = {
    audio: true,
    video: true,
  };
  private mediaDevices: MediaDeviceInfo[] | null = null;
  selectedDevices = {
    audioinput: "default",
    audiooutput: "default",
    videoinput: "",
  };

  private briefingData: TrainingSessionData["briefing"] | null = null;
  private liveSessionToken: string | null = null;
  private avatar: AvaturnHead | null = null;
  private avatarInitFailed: boolean = false;

  constructor(
    @inject(LandingServiceID.Timer) readonly timer: IRemainingTime,
    @inject(LandingServiceID.CoachStore) readonly coach: ICoachManager,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get devices(): IMediaDevices | null {
    if (!this.mediaDevices) return null;

    return this.mediaDevices.reduce((acc, device) => {
      if (!acc[device.kind]) {
        acc[device.kind] = [];
      }
      acc[device.kind].push(device);
      return acc;
    }, {} as IMediaDevices);
  }

  get briefing(): TrainingSessionData["briefing"] | null {
    return this.briefingData;
  }

  async createSession(scenarioType: string): Promise<TrainingSessionData> {
    const data = await createPublicTrainingSession({ scenarioType });

    runInAction(() => {
      this.sessionId = data.id;
      this.briefingData = data.briefing;
      this.liveSessionToken = data.live_session_token;
    });

    trackSessionEvent("session_created", {
      sessionId: data.id,
      scenarioType,
    });

    return data;
  }

  /**
   * Initialize session - Request media devices and prepare for join
   * This method is idempotent - safe to call multiple times
   *
   * Behavior:
   * - If sessionId doesn't match: logs warning and returns
   * - If already initialized (ready/started/finished): does nothing
   * - If in initial state: performs initialization
   * - If in media-error state: does nothing
   */
  async init({ trainingId }: { trainingId: string }): Promise<void> {
    if (this.sessionId && this.sessionId !== trainingId) {
      throw new Error(
        `Session ID mismatch: expected '${trainingId}', got '${this.sessionId}'.`,
      );
    }

    if (this.state === "media-error") {
      throw new Error(
        "Cannot initialize: previous media error. Call cleanupSession() first.",
      );
    }

    if (this.avatar) {
      return;
    }

    try {
      runInAction(() => {
        this.state = "initial";
        this.sessionId = trainingId;
      });

      await this.requestMediaDevices();

      if (!this.liveSessionToken) {
        throw new Error("Session token not found. Create session first.");
      }

      const avatar = new AvaturnHead({
        sessionToken: this.liveSessionToken,
        immediatelyJoin: false,
        apiHost:
          process.env.NEXT_PUBLIC_CONFIGURATION === "staging"
            ? "https://api.staging.avaturn.live"
            : "https://api.avaturn.live",
        audioSource: true,
      });

      avatar.on("avatar_started_speaking", () => {
        runInAction(() => {
          this.isAvatarSpeaking = true;
        });
      });
      avatar.on("avatar_ended_speaking", () => {
        runInAction(() => {
          this.isAvatarSpeaking = false;
        });
      });
      avatar.on("event", ({ value }) => {
        if (!value?.data?.event) return;

        switch (value.data.event) {
          case "coach_mode_activated":
            this.coach.startSpeaking();
            break;
          case "coach_mode_deactivated":
            this.coach.stopSpeaking();
            break;
          case "coach_text":
            this.appendCoachText(value.data);
            break;
        }
      });

      avatar.init().catch((error) => {
        console.error("[LandingSessionStore] Avatar init failed:", error);
        runInAction(() => {
          this.avatarInitFailed = true;
        });
      });

      runInAction(() => {
        this.avatar = avatar;
        this.avatarMediaStream = avatar.mediaStream || null;
        this.state = "ready";
      });
    } catch (error) {
      console.error("[LandingSessionStore] Init failed:", error);
      runInAction(() => {
        this.state = "media-error";
      });
      throw error;
    }
  }

  // One box per turn: sentences arrive one message at a time and grow the box
  // opened by the turn's first sentence, which also fixes its timestamp.
  private appendCoachText(d: {
    segment_uid: string;
    text: string;
    elapsed_ms: number;
  }): void {
    // Sentences arrive without surrounding whitespace, so join them with a space.
    // The LLM writes markdown emphasis the TTS just reads over; drop the markers.
    const clean = d.text.replace(/\*/g, "").trim();
    if (!clean) return;

    const last = this.coachTurns.at(-1);
    if (last?.segmentUid === d.segment_uid) {
      last.text = `${last.text} ${clean}`;
      return;
    }

    this.coachTurns.push({
      segmentUid: d.segment_uid,
      text: clean,
      elapsedMs: d.elapsed_ms,
    });
  }

  async join(): Promise<void> {
    if (this.state !== "ready") {
      throw new Error(
        `Cannot join session: invalid state '${this.state}'. Expected 'ready'.`,
      );
    }

    if (!this.avatar) {
      throw new Error("Avatar not initialized. Call init() first.");
    }

    if (!this.sessionId) {
      throw new Error("No session ID. Create session first.");
    }

    // `avatar.init()` is started in init() but cannot be awaited there: its
    // promise settles only after the avatar's first video frame, which arrives
    // after join(). Wait for the session it fetches instead, so clicking the
    // moment the button unlocks doesn't hit "Session not initialized".
    await waitForAvatarSession(this.avatar, {
      isAborted: () => this.avatarInitFailed,
    });

    const shouldEnableAudio = this.mediaPermissions.audio;

    await this.avatar.join({ startAudioOff: true });

    if (shouldEnableAudio) {
      this.avatar.toggleLocalAudio(true);
    }

    this.timer.startTimer();
    this.timer.on("end", this.end);

    runInAction(() => {
      this.state = "started";
    });

    trackSessionEvent("session_started", {
      sessionId: this.sessionId,
    });
  }

  async end(): Promise<void> {
    if (this.state !== "started") {
      throw new Error(
        `Cannot end session: invalid state '${this.state}'. Expected 'started'.`,
      );
    }

    if (!this.sessionId) {
      throw new Error("No session ID to end.");
    }

    const sessionId = this.sessionId;

    this.timer.off("end", this.end);
    this.timer.stopTimer();

    runInAction(() => {
      this.state = "finished";
    });

    trackSessionEvent("session_ended", {
      sessionId,
    });

    this.stopMediaStreams();

    if (this.avatar) {
      this.avatar.dispose?.();
    }

    if (sessionId) {
      await completePublicTrainingSession(sessionId);
    }
  }

  setMediaPermission(
    target: keyof ILandingSessionManager["mediaPermissions"],
    value: boolean,
  ): void {
    runInAction(() => {
      this.mediaPermissions[target] = value;
    });

    if (this.sessionId) {
      trackSessionEvent("media_permission_changed", {
        sessionId: this.sessionId,
        mediaType: target,
        enabled: value,
      });
    }

    if (!this.avatar) return;

    if (target === "audio") {
      this.avatar.toggleLocalAudio(value);
    }
  }

  async changeMediaDevice(
    type: "audiooutput" | "videoinput" | "audioinput",
    id: string,
  ): Promise<void> {
    runInAction(() => {
      this.selectedDevices[type] = id;
    });

    if (!this.avatar) return;

    if (type === "audiooutput") {
      await this.avatar.setOutputDevice({
        outputDeviceId: id,
      });
    } else {
      await this.avatar.setInputDevices({
        audioDeviceId: type === "audioinput" ? id : undefined,
        videoDeviceId: type === "videoinput" ? id : undefined,
      });
    }
  }

  dispose(): void {
    this.stopMediaStreams();

    if (this.avatar) {
      this.avatar.dispose?.();
    }

    this.timer.off("end", this.end);
    this.timer.dispose();

    runInAction(() => {
      this.sessionId = null;
      this.briefingData = null;
      this.liveSessionToken = null;
      this.state = "initial";
      this.mediaDevices = null;
      this.avatar = null;
      this.avatarInitFailed = false;
      this.isAvatarSpeaking = false;
      this.coachTurns = [];
    });

    this.coach.reset();
  }

  private async requestMediaDevices(): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: "user",
      },
      audio: true,
    });

    runInAction(() => {
      this.userVideoStream = stream;
    });

    const devices = await navigator.mediaDevices.enumerateDevices();
    const filteredDevices = devices.filter((d) => !!d.deviceId);

    const hasAudioInput = filteredDevices.some((d) => d.kind === "audioinput");

    if (!hasAudioInput) {
      throw new Error("No audio input device found");
    }

    runInAction(() => {
      this.mediaDevices = filteredDevices;

      const videoDevice = filteredDevices.find((d) => d.kind === "videoinput");
      if (videoDevice) {
        this.selectedDevices.videoinput = videoDevice.deviceId;
      }
    });
  }

  private stopMediaStreams(): void {
    if (this.userVideoStream) {
      this.userVideoStream.getTracks().forEach((track) => track.stop());
      this.userVideoStream = null;
    }

    if (
      this.avatarMediaStream &&
      this.avatarMediaStream !== this.userVideoStream
    ) {
      this.avatarMediaStream.getTracks().forEach((track) => track.stop());
      this.avatarMediaStream = null;
    }
  }
}
